/**
 * 国优炬康 管理后台 Auth Store (Pinia)
 *
 * 管理:
 * - token / admin 信息持久化(localStorage)
 * - 当前角色切换(G5-DEV-015 工作台 8 角色切换器)
 * - 权限点列表
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export type AdminInfo = {
  id: string;
  username: string;
  realName: string;
  role: string;
  roleName: string;
  avatar?: string;
  permissions: string[];
};

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string>(localStorage.getItem('gyjk_admin_token') || '');
  const admin = ref<AdminInfo | null>(JSON.parse(localStorage.getItem('gyjk_admin_info') || 'null'));

  const isLoggedIn = computed(() => !!token.value);
  const permissions = computed(() => admin.value?.permissions ?? []);
  const currentRole = computed(() => admin.value?.role ?? '');

  function setAuth(authData: { token: string; admin: AdminInfo }) {
    token.value = authData.token;
    admin.value = authData.admin;
    localStorage.setItem('gyjk_admin_token', authData.token);
    localStorage.setItem('gyjk_admin_info', JSON.stringify(authData.admin));
  }

  function clearAuth() {
    token.value = '';
    admin.value = null;
    localStorage.removeItem('gyjk_admin_token');
    localStorage.removeItem('gyjk_admin_info');
  }

  /**
   * 切换当前角色(G5-DEV-015 — 工作台 8 角色切换器)
   * @param newRole 新角色
   */
  function switchRole(newRole: { role: string; roleName: string; permissions: string[] }) {
    if (!admin.value) return;
    admin.value = {
      ...admin.value,
      role: newRole.role,
      roleName: newRole.roleName,
      permissions: newRole.permissions,
    };
    localStorage.setItem('gyjk_admin_info', JSON.stringify(admin.value));
  }

  function hasPermission(permission: string): boolean {
    if (!admin.value) return false;
    if (admin.value.permissions.includes('*')) return true;
    return admin.value.permissions.includes(permission);
  }

  return {
    token,
    admin,
    isLoggedIn,
    permissions,
    currentRole,
    setAuth,
    clearAuth,
    switchRole,
    hasPermission,
  };
});
